import { Button, Chip, makeStyles, Theme, Typography } from '@material-ui/core';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { Entity } from '@backstage/catalog-model';
import {
  getToolDescription,
  getToolDocLink,
  getToolOpenLink,
  getToolRepoLink,
  getToolTags,
  getToolTitle,
} from './utils';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(128px, 24%) 176px',
    gap: theme.spacing(1.5),
    alignItems: 'center',
    padding: theme.spacing(0.875, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 0,
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'minmax(0, 1fr) auto',
      gap: theme.spacing(1),
    },
  },
  main: {
    gridColumn: 1,
    minWidth: 0,
  },
  title: {
    overflowWrap: 'anywhere',
    lineHeight: 1.25,
  },
  description: {
    overflowWrap: 'anywhere',
    lineHeight: 1.3,
    marginTop: theme.spacing(0.25),
  },
  tags: {
    gridColumn: 2,
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    minWidth: 0,
    [theme.breakpoints.down('sm')]: {
      gridColumn: '1 / -1',
      gridRow: 2,
    },
  },
  chip: {
    maxWidth: 128,
    height: 22,
    '& .MuiChip-label': {
      fontSize: 12,
      paddingLeft: theme.spacing(0.75),
      paddingRight: theme.spacing(0.75),
    },
  },
  actions: {
    gridColumn: 3,
    display: 'grid',
    gridTemplateColumns: '52px 44px 72px',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    width: 176,
    justifySelf: 'end',
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('sm')]: {
      gridColumn: 2,
    },
  },
  action: {
    minWidth: 0,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  hiddenAction: {
    visibility: 'hidden',
    pointerEvents: 'none',
  },
}));

type ToolItemProps = {
  tool: Entity;
};

export const ToolItem = ({ tool }: ToolItemProps) => {
  const classes = useStyles();
  const title = getToolTitle(tool);
  const description = getToolDescription(tool);
  const tags = getToolTags(tool).slice(0, 3);
  const repoLink = getToolRepoLink(tool);
  const docLink = getToolDocLink(tool);
  const openLink = getToolOpenLink(tool);
  const actions = [
    { label: 'Repo', url: repoLink?.url },
    { label: 'Doc', url: docLink?.url },
    { label: 'Open', url: openLink?.url, isPrimary: true },
  ];

  return (
    <div className={classes.root}>
      <div className={classes.main}>
        <Typography
          variant="subtitle2"
          component="h3"
          className={classes.title}
        >
          {title}
        </Typography>
        {description && (
          <Typography
            variant="body2"
            color="textSecondary"
            className={classes.description}
          >
            {description}
          </Typography>
        )}
      </div>
      {tags.length > 0 && (
        <div className={classes.tags} aria-label={`${title} tags`}>
          {tags.map(tag => (
            <Chip key={tag} label={tag} size="small" className={classes.chip} />
          ))}
        </div>
      )}
      <div className={classes.actions}>
        {actions.map(action => (
          <Button
            key={action.label}
            className={`${classes.action} ${
              action.url ? '' : classes.hiddenAction
            }`}
            color="primary"
            size="small"
            href={action.url ?? '#'}
            target="_blank"
            rel="noopener noreferrer"
            endIcon={action.isPrimary ? <ArrowForwardIcon /> : undefined}
            aria-hidden={!action.url}
            tabIndex={action.url ? undefined : -1}
            aria-label={`${action.label} ${title}`}
          >
            {action.label}
          </Button>
        ))}
      </div>
    </div>
  );
};
